import { GQL_ID, Order } from '@@types'
import { ORDER } from '@hive/status'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'
import { ReactSVG } from 'react-svg'

import { useMutateChoosePaymentMethodForOrder } from '@entities/Order/api/choosePaymentMethodForOrder.gql'

import { useModalStore } from '@features/modal'

import PaymentModal from '@widgets/payment-modal/ui/PaymentModal'
import SlotsModal from '@widgets/slots/ui/modal/modal'
import styles from '@widgets/slots/ui/modal/styles.module.scss'

import useSBPBanks from '@shared/hooks/useSBPBanks'
import Button from '@shared/ui/button'
import { formatPrice } from '@shared/uttils'
import { formatDate } from '@shared/uttils/formatDate'
import { getOrderStatusLabel } from '@shared/uttils/order'

import orderStyles from './styles.module.scss'


export default function OrderHeader ({
  order,
  isDetail,
  handleDetailClick,
  onSlotUpdate,
}: {
  order: Order
  isDetail: boolean
  handleDetailClick: () => void
  onSlotUpdate?: () => void
}) {
  const { t: to } = useTranslation('order')
  const { t: tc } = useTranslation('common')
  const { label, color } = getOrderStatusLabel(order.status, to)
  const { openModal } = useModalStore()
  const [ choosePaymentMethodForOrder ] = useMutateChoosePaymentMethodForOrder()
  const { staticBanks } = useSBPBanks()

  // Изменение слота и способа оплаты доступно только для подтвержденных заказов
  const canChangeSlot = order.status === ORDER.CONFIRMED
  const canChangePayment = order.status === ORDER.CONFIRMED && !!order.paymentMethod

  // Форматирование способа оплаты
  const getPaymentMethodDisplay = (): { value: string; icon?: string } => {
    if (!order.paymentMethod) return { value: '' }

    // СБП
    if (order.paymentMethod.type === 'sbp') {
      const bank = staticBanks.find((b) => b.schema === `bank${order.paymentMethod?.last}`)
      return {
        value: bank ? bank.name : `СБП ${order.paymentMethod.last || ''}`,
        icon: bank ? bank.logo : '/icons/payments/sbp.svg',
      }
    }

    // Карта
    return { value: `${to('card')} ****  ${order.paymentMethod.last || ''}` }
  }

  const paymentDisplay = getPaymentMethodDisplay()

  const handleTimeDeliveryClick = () => {
    if (!canChangeSlot) return
    
    openModal('slots', <SlotsModal orderId={order.id} onUpdate={onSlotUpdate} />, {
      title: tc('slots.title'),
      description: tc('slots.description'),
      classes: {
        body: styles['modal-body'],
        content: styles['modal-content'],
      },
      disabledScroll: true,
    })
  }

  const handlePaymentMethodClick = () => {
    if (!canChangePayment) return

    openModal(
      'changeOrderPayment',
      <PaymentModal
        hideNewMethods={true}
        onChoose={async (paymentTokenId: GQL_ID) => {
          if (paymentTokenId !== 'new-card') {
            try {
              await choosePaymentMethodForOrder({
                variables: {
                  orderId: order.id,
                  paymentMethodId: paymentTokenId
                }
              })
              if (onSlotUpdate) {
                onSlotUpdate()
              }
            }
            catch {
              // Error handling
            }
          }
        }}
        current={order.payment_token_id ? String(order.payment_token_id) : null}
      />,
      {
        title: to('order.payment-variant'),
        classes: {
          body: styles['modal-body'],
          content: styles['modal-content'],
        },
        disabledScroll: true,
      },
    )
  }

  return (
    <div
      className={classNames(orderStyles.header, {
        [orderStyles['header--active']]: isDetail,
      })}
    >
      <div className={orderStyles.header__order}>
        <div className={orderStyles.header__title}>
          <h4>
            {to('order')} <span data-green>№ {order.id}</span>
          </h4>
          <span>{ order.deliverySlot ? order.deliverySlot.slotDate + ' ' + order.deliverySlot.slotTime : formatDate(order?.placed_at) }</span>
        </div>
        <div className={orderStyles.header__control}>
          {!isDetail && <h3> {formatPrice(order.total)}</h3>}
          <span data-color={color} className={orderStyles.header__status}>
            {label}
          </span>
        </div>
        <Button
          onClick={handleDetailClick}
          className={classNames(orderStyles.header__button, {
            [orderStyles['header__button--active']]: isDetail,
          })}
          theme="grey"
          size="small"
        >
          <ReactSVG src="/icons/arrow-right.svg" wrapper="span" />
        </Button>
      </div>

      {isDetail && (
        <>
          <div className={orderStyles.header__data}>
            {order.recipientInfo?.firstName && (
              <InfoRow
                label={to('order.user')}
                value={order.recipientInfo?.firstName as string}
              />
            )}

            <InfoRow
              label={to('order.address')}
              value={order.address}
            />
            <InfoRow
              label={to('order.time-delivery')}
              value={order.deliverySlot ? `${order.deliverySlot?.slotDate} ${order.deliverySlot?.slotTime}` : ''}
              isPrimary={canChangeSlot}
              isClickable={canChangeSlot}
              onClick={canChangeSlot ? handleTimeDeliveryClick : undefined}
            />
            {order.paymentMethod && (
              <InfoRow
                label={to('order.payment-variant')}
                value={paymentDisplay.value}
                icon={paymentDisplay.icon}
                isHighlight={canChangePayment}
                isClickable={canChangePayment}
                onClick={canChangePayment ? handlePaymentMethodClick : undefined}
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

function InfoRow ({
  label,
  value,
  isHighlight = false,
  isPrimary = false,
  isClickable = false,
  onClick,
  icon,
}: {
  label: string;
  value: string;
  isHighlight?: boolean;
  isPrimary?: boolean;
  isClickable?: boolean;
  onClick?: () => void;
  icon?: string;
}) {
  return (
    <p>
      {label}:{' '}
      <span
        data-green={isHighlight ? true : undefined}
        data-primary={isPrimary ? true : undefined}
        className={classNames(
          { [orderStyles.clickable]: isClickable },
          { [orderStyles['with-icon']]: !!icon }
        )}
        onClick={isClickable ? onClick : undefined}
      >
        {icon && <img src={icon} alt="" className={orderStyles.icon} />}
        {value}
      </span>
    </p>
  )
}